/* eslint-disable max-lines */
import React, { useContext, useState, useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Table, Badge } from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import PayrollService from '../../../../services/Payroll';
import { CoverSheetData } from './exportCoverSheet';
import { isAdmin } from '../../../../helpers';
import { downloadFromUrl } from '../../../../helpers/downloadFromUrl';

interface CoverSheetModalProps {
  isOpen: boolean;
  toggle: () => void;
  coverSheetStartDate: string;
  coverSheetEndDate: string;
  onStatusChange?: () => void;
}

interface CoverSheetStatus {
  exists: boolean;
  id?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  approvedAt?: string | null;
  approvedBy?: string | null;
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  rejectionReason?: string | null;
  notes?: string | null;
  submittedAt?: string | null;
}

const buildFilterParams = (filters: Array<{ field: string; operation: string; value: any }>) => {
  const params: Record<string, string> = {};
  filters.forEach((filter, index) => {
    params[`filters[${index}][field]`] = filter.field;
    params[`filters[${index}][operation]`] = filter.operation;
    params[`filters[${index}][value]`] = String(filter.value);
  });
  return params;
};

const CoverSheetModal = ({
  isOpen,
  toggle,
  coverSheetStartDate,
  coverSheetEndDate,
  onStatusChange,
}: CoverSheetModalProps) => {
  const { token, currentSchool, user }: any = useContext(AuthContext);
  const [data, setData] = useState<CoverSheetData>({
    bonuses: [],
    deductions: [],
    raises: [],
    extraPayments: [],
    terminatedEmployees: [],
    newEmployees: [],
  });
  const [loading, setLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>(Date.now());
  const [status, setStatus] = useState<CoverSheetStatus>({ exists: false });
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const isAdminUser = isAdmin(user);

  const handleExportToExcel = async () => {
    if (!currentSchool?.value) return;
    setExportLoading(true);
    try {
      const periodStart = moment(coverSheetStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const periodEnd = moment(coverSheetEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const response = await PayrollService(token).payrollDownload(currentSchool.value, periodStart, periodEnd, 'xlsx');
      const downloadUrl = response?.data?.downloadUrl;
      if (downloadUrl) {
        const schoolLabel = (currentSchool?.label || 'School').replace(/\s+/g, '_');
        const filename = `CoverSheet_${periodStart}_${periodEnd}_${schoolLabel}.xlsx`;
        await downloadFromUrl(downloadUrl, filename);
      } else {
        toast.error('Error getting download link');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to export to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchCoverSheetStatus = async () => {
    if (!coverSheetStartDate || !coverSheetEndDate || !currentSchool?.value) {
      return;
    }

    try {
      const startDate = moment(coverSheetStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const endDate = moment(coverSheetEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const result = await PayrollService(token).coverSheetGetStatus(currentSchool.value, startDate, endDate);
      setStatus(result?.data || { exists: false });
    } catch (error) {
      setStatus({ exists: false });
      toast.error('Failed to fetch Cover Sheet status');
    }
  };

  const handleApprove = async () => {
    if (!coverSheetStartDate || !coverSheetEndDate || !currentSchool?.value) {
      return;
    }

    setActionLoading(true);
    try {
      const startDate = moment(coverSheetStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const endDate = moment(coverSheetEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD');

      await PayrollService(token).coverSheetApprove(currentSchool.value, {
        periodStart: startDate,
        periodEnd: endDate,
      });

      toast.success('Cover Sheet approved successfully!');
      await fetchCoverSheetStatus();
      if (onStatusChange) onStatusChange();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to approve Cover Sheet');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = prompt('Enter rejection reason:');
    if (!reason || !coverSheetStartDate || !coverSheetEndDate || !currentSchool?.value) {
      return;
    }

    setActionLoading(true);
    try {
      const startDate = moment(coverSheetStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const endDate = moment(coverSheetEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD');

      await PayrollService(token).coverSheetReject(currentSchool.value, {
        periodStart: startDate,
        periodEnd: endDate,
        rejectionReason: reason,
      });

      toast.success('Cover Sheet rejected');
      await fetchCoverSheetStatus();
      if (onStatusChange) onStatusChange();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to reject Cover Sheet');
    } finally {
      setActionLoading(false);
    }
  };

  const getButtonStates = () => {
    if (!status.exists) {
      return {
        showApproveButton: true,
        showRejectButton: true,
        approveDisabled: false,
        rejectDisabled: false,
        statusBadge: null,
      };
    }

    if (status.status === 'APPROVED') {
      return {
        showApproveButton: false,
        showRejectButton: false,
        approveDisabled: true,
        rejectDisabled: true,
        statusBadge: 'APPROVED',
        statusBadgeColor: 'success',
      };
    }

    if (status.status === 'REJECTED') {
      return {
        showApproveButton: true,
        showRejectButton: true,
        approveDisabled: false,
        rejectDisabled: false,
        statusBadge: 'REJECTED',
        statusBadgeColor: 'danger',
        rejectionReason: status.rejectionReason,
      };
    }

    return {
      showApproveButton: true,
      showRejectButton: true,
      approveDisabled: false,
      rejectDisabled: false,
    };
  };

  useEffect(() => {
    const fetchCoverSheetData = async () => {
      if (!isOpen || !coverSheetStartDate || !coverSheetEndDate || !currentSchool?.value) {
        return;
      }

      setLoading(true);
      try {
        fetchCoverSheetStatus();
        const startDate = moment(coverSheetStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
        const endDate = moment(coverSheetEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD');

        const [bonuses, deductions, raises, terminatedEmployees, newEmployees, timecardsResult, extraPayments] =
          await Promise.all([
            PayrollService(token).coverSheetBonuses(currentSchool.value, {
              periodStart: startDate,
              periodEnd: endDate,
            }),
            PayrollService(token).coverSheetDeductions(currentSchool.value, {
              periodStart: startDate,
              periodEnd: endDate,
            }),
            PayrollService(token).coverSheetRaises(currentSchool.value, {
              limit: 100,
              ...buildFilterParams([
                { field: 'dateItBecomesEffectiveOn', operation: '>=', value: startDate },
                { field: 'dateItBecomesEffectiveOn', operation: '<=', value: endDate },
                { field: 'status', operation: '=', value: 'APPROVE' },
              ]),
            }),
            PayrollService(token).coverSheetTerminatedEmployees(currentSchool.value, {
              limit: 100,
              ...buildFilterParams([
                { field: 'lastWorkDay', operation: '>=', value: startDate },
                { field: 'lastWorkDay', operation: '<=', value: endDate },
              ]),
            }),
            PayrollService(token).coverSheetNewEmployees(currentSchool.value, {
              limit: 100,
              ...buildFilterParams([
                { field: 'startDate', operation: '>=', value: startDate },
                { field: 'startDate', operation: '<=', value: endDate },
              ]),
            }),
            PayrollService(token).timecardListWithPending(currentSchool.value, {
              weekStartDate: startDate,
              weekEndDate: endDate,
              page: 1,
              limit: 9999,
              'filters[field]': 'status',
              'filters[value]': 'ACTIVE',
            }),
            PayrollService(token)
              .coverSheetExtraPayments(currentSchool.value, {
                limit: 100,
                ...buildFilterParams([
                  { field: 'dateAddingToPayroll', operation: '>=', value: startDate },
                  { field: 'dateAddingToPayroll', operation: '<=', value: endDate },
                ]),
              })
              .catch(() => ({ data: [] })),
          ]);

        setData({
          bonuses: bonuses?.data || [],
          deductions: deductions?.data || [],
          raises: raises?.data || [],
          extraPayments: extraPayments?.data || [],
          terminatedEmployees: terminatedEmployees?.data || [],
          newEmployees: newEmployees?.data || [],
          timecards: timecardsResult?.data || [],
        });
      } catch (error) {
        toast.error('Failed to load Cover Sheet data');
      } finally {
        setLoading(false);
      }
    };

    fetchCoverSheetData();
  }, [isOpen, coverSheetStartDate, coverSheetEndDate, currentSchool?.value, token, lastUpdatedAt]);
  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle} size="xl" style={{ maxWidth: '70vw' }}>
      <ModalBody>
        <div
          onClick={toggle}
          className="d-flex p-1 cursor-pointer"
          style={{ position: 'absolute', top: 20, right: 20 }}
        >
          <i className="fas fa-times" />
        </div>
        <div className="p-2">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className="text-primary m-0">Cover Sheet</h1>
            {getButtonStates().statusBadge && (
              <Badge
                color={getButtonStates().statusBadgeColor}
                pill
                style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}
              >
                {getButtonStates().statusBadge}
              </Badge>
            )}
          </div>

          {status.status === 'REJECTED' && status.rejectionReason && (
            <div className="alert alert-danger" role="alert">
              <strong>Rejection Reason:</strong> {status.rejectionReason}
            </div>
          )}

          <div className="d-flex align-items-center" style={{ gap: 20 }}>
            <h3 className="text-primary">Payroll Period:</h3>
            <h3>
              {coverSheetStartDate} » {coverSheetEndDate}
            </h3>
          </div>

          <hr className="my-3" />

          <div className="d-flex align-items-center" style={{ gap: 20 }}>
            <h3 className="text-primary">Approved Date:</h3>
            <h3>{status.approvedAt ? moment(status.approvedAt).format('MM/DD/YYYY') : '-'}</h3>
          </div>

          <hr className="my-3" />

          <div className="d-flex align-items-center" style={{ gap: 20 }}>
            <h3 className="text-primary">Submission Date:</h3>
            <h3>{status.submittedAt ? moment(status.submittedAt).format('MM/DD/YYYY') : '-'}</h3>
          </div>

          <hr className="my-3" />

          <div className="d-flex align-items-center" style={{ gap: 20 }}>
            <h3 className="text-primary">Location:</h3>
            <h3>{currentSchool.label}</h3>
          </div>

          <hr className="my-3" />

          <div className="d-flex flex-column justify-content-center" style={{ gap: 20 }}>
            <h3 className="text-primary m-0 p-0">New Employees:</h3>
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Title</th>
                    <th>Rate</th>
                    <th>Frequency</th>
                    <th>Start Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.newEmployees.length > 0 ? (
                    data.newEmployees.map((employee: any) => (
                      <tr key={employee.id}>
                        <td>
                          {employee.user?.lastName}, {employee.user?.firstName}
                        </td>
                        <td>{employee.title?.name || '-'}</td>
                        <td>
                          ${' '}
                          {employee.currentPayRate?.payRate
                            ? parseFloat(employee.currentPayRate.payRate).toFixed(2)
                            : employee.payRate
                            ? parseFloat(employee.payRate).toFixed(2)
                            : '0.00'}
                        </td>
                        <td>{employee.currentPayRate?.paymentFrequency || employee.paymentFrequency || '-'}</td>
                        <td>{moment(employee.startDate).format('MM/DD/YYYY')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No new employees in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>

          <hr className="my-3" />

          <div className="d-flex flex-column justify-content-center" style={{ gap: 20 }}>
            <h3 className="text-primary m-0 p-0">Terminated Employees:</h3>
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Reason</th>
                    <th>Last Work Day</th>
                  </tr>
                </thead>
                <tbody>
                  {data.terminatedEmployees.length > 0 ? (
                    data.terminatedEmployees.map((termination: any) => (
                      <tr key={termination.id}>
                        <td>
                          {termination.employee?.user?.lastName}, {termination.employee?.user?.firstName}
                        </td>
                        <td>{termination.category || '-'}</td>
                        <td>{termination.reason || '-'}</td>
                        <td>{moment(termination.lastWorkDay).format('MM/DD/YYYY')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        No terminated employees in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>

          <hr className="my-3" />

          <div className="d-flex flex-column justify-content-center" style={{ gap: 20 }}>
            <h3 className="text-primary m-0 p-0">Deductions:</h3>
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Amount (This Paycheck)</th>
                    <th>Installment</th>
                    <th>Frequency</th>
                  </tr>
                </thead>
                <tbody>
                  {data.deductions.length > 0 ? (
                    data.deductions.map((deduction: any) => {
                      const perPaycheck = parseFloat(deduction.amountDeducted || 0);
                      const total = parseFloat(deduction.amount || 0);
                      const paid = parseFloat(deduction.amountPaid || 0);
                      const totalInstallments = perPaycheck > 0 ? Math.ceil(total / perPaycheck) : 0;
                      const currentInstallment =
                        perPaycheck > 0 ? Math.min(Math.floor(paid / perPaycheck) + 1, totalInstallments) : 0;

                      return (
                        <tr key={deduction.id}>
                          <td>
                            {deduction.user?.lastName}, {deduction.user?.firstName}
                          </td>
                          <td>{deduction.type || '-'}</td>
                          <td>
                            $ {parseFloat(deduction.amountDeducted ?? deduction.amount ?? 0).toFixed(2)}
                            {deduction.amountDeducted != null && (
                              <span className="text-muted"> / $ {total.toFixed(2)} total</span>
                            )}
                          </td>
                          <td>{totalInstallments > 0 ? `${currentInstallment} / ${totalInstallments}` : '-'}</td>
                          <td>{deduction.frequency || '-'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No deductions in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>

          <hr className="my-3" />

          <div className="d-flex flex-column justify-content-center" style={{ gap: 20 }}>
            <h3 className="text-primary m-0 p-0">Raises:</h3>
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Reason</th>
                    <th>Old Amount</th>
                    <th>New Amount</th>
                    <th>Effective Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.raises.length > 0 ? (
                    data.raises.map((raise: any) => (
                      <tr key={raise.id}>
                        <td>
                          {raise.user?.lastName}, {raise.user?.firstName}
                        </td>
                        <td>{raise.reason || '-'}</td>
                        <td>$ {parseFloat(raise.oldAmount || 0).toFixed(2)}</td>
                        <td>$ {parseFloat(raise.amount || 0).toFixed(2)}</td>
                        <td>{moment(raise.dateItBecomesEffectiveOn).format('MM/DD/YYYY')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No raises in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
          <hr className="my-3" />

          <div className="d-flex flex-column justify-content-center" style={{ gap: 20 }}>
            <h3 className="text-primary m-0 p-0">Extra Payments:</h3>
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Reason</th>
                    <th>Amount</th>
                    <th>Date Adding To Payroll</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.extraPayments || []).length > 0 ? (
                    (data.extraPayments || []).map((payment: any) => (
                      <tr key={payment.id}>
                        <td>
                          {payment.user?.lastName}, {payment.user?.firstName}
                        </td>
                        <td>{payment.type || '-'}</td>
                        <td>{payment.reason || '-'}</td>
                        <td>$ {parseFloat(payment.amount || 0).toFixed(2)}</td>
                        <td>{moment(payment.dateAddingToPayroll).utc().format('MM/DD/YYYY')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No extra payments in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
          <hr className="my-3" />

          <div className="d-flex flex-column justify-content-center" style={{ gap: 20 }}>
            <h3 className="text-primary m-0 p-0">Bonuses:</h3>
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Reason</th>
                    <th>Amount (This Paycheck)</th>
                    <th>Installment</th>
                    <th>Effective Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bonuses.length > 0 ? (
                    data.bonuses.map((bonus: any) => {
                      const total = parseFloat(bonus.amount || 0);
                      const perPayment = parseFloat(bonus.amountPerPayment || 0);
                      const paid = parseFloat(bonus.amountPaid || 0);
                      const totalInstallments = bonus.numberOfPayments ? Number(bonus.numberOfPayments) : 0;
                      const currentInstallment =
                        totalInstallments > 0 && perPayment > 0
                          ? Math.min(Math.floor(paid / perPayment) + 1, totalInstallments)
                          : 0;

                      return (
                        <tr key={bonus.id}>
                          <td>
                            {bonus.user?.lastName}, {bonus.user?.firstName}
                          </td>
                          <td>{bonus.reason || '-'}</td>
                          <td>
                            $ {(perPayment > 0 ? perPayment : total).toFixed(2)}
                            {perPayment > 0 && <span className="text-muted"> / $ {total.toFixed(2)} total</span>}
                          </td>
                          <td>{totalInstallments > 0 ? `${currentInstallment} / ${totalInstallments}` : '-'}</td>
                          <td>{moment(bonus.dateItBecomesEffectiveOn).format('MM/DD/YYYY')}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No bonuses in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          <div className="d-flex" style={{ gap: 10 }}>
            <Button color="info" onClick={() => setLastUpdatedAt(Date.now())} disabled={loading}>
              <i className="fas fa-sync-alt mr-2" />
              Reload Data
            </Button>
            <Button color="success" onClick={handleExportToExcel} disabled={loading || exportLoading}>
              <i className="fas fa-file-excel mr-2" />
              Export to Excel
            </Button>
          </div>
          <div className="d-flex" style={{ gap: 10 }}>
            {isAdminUser && getButtonStates().showRejectButton && (
              <Button
                color="danger"
                onClick={handleReject}
                disabled={actionLoading || loading || getButtonStates().rejectDisabled}
              >
                <i className="fas fa-times mr-2" />
                Reject
              </Button>
            )}
            {isAdminUser && getButtonStates().showApproveButton && (
              <Button
                color="primary"
                onClick={handleApprove}
                disabled={actionLoading || loading || getButtonStates().approveDisabled}
              >
                <i className="fas fa-check mr-2" />
                Approve
              </Button>
            )}
            <Button color="secondary" onClick={toggle}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default CoverSheetModal;
